import { ModalHandler } from "@lib/ModalHandler";
import { ModalContext } from "@lib/context/ModalContext";
import { getApiKeyFromStorage, validateApiKey } from "@lib/util/api";
import { MutationObserverCustom, SHOW_MODAL_EVENT, isIncognito } from "@lib/util/common";
import { VALID_HOSTNAMES, isHostnameValid, startFillAgnostic } from "@lib/util/form_util_agnostic";
import { createSignal, onMount, useContext } from "solid-js";
import { ModalBody } from "./components/ModalBody";
import { ModalFooter } from "./components/ModalFooter";
import { autoScrollToNextAfterPhotoUpload, handleEnterOnTextInput } from "./qtrax_utils/form_handlers";
import { getSOID, isLocationMainServiceOrderMenu } from "./qtrax_utils/form_util";

export function App() {
  const context = useContext(ModalContext)!;
  const [modalHandler, setModalHandler] = createSignal(new ModalHandler(context, "mm-id-open-modal-btn"));

  function resetModal() {
    console.log("Resetting modalhandler");

    context.resetContext();
    setModalHandler(() => new ModalHandler(context, "mm-id-open-modal-btn"));
  }

  async function handleModalStartup() {
    console.log("Running modal startup procedure");

    resetModal();
    modalHandler().showModal();

    if (!isHostnameValid()) {
      modalHandler().clearAllStatus();
      modalHandler().addErrorMsg(
        `Skipping autofill. Current page is 
        ${new URL(document.location.href).hostname}, but expected ${VALID_HOSTNAMES.join(", ")}.`
      );
      return;
    }

    if (!isLocationMainServiceOrderMenu()) {
      modalHandler().clearAllStatus();
      modalHandler().addErrorMsg("Current page is not the main Service Order Menu");
      return;
    }

    modalHandler().context.setSoid(getSOID());

    modalHandler().clearFillStatus();
    modalHandler().addFillStatusMsg("Checking API token validity");

    const isIncognitoMode = await isIncognito();

    const apiKey = getApiKeyFromStorage();
    if (apiKey === null) {
      modalHandler().clearFillStatus();
      if (isIncognitoMode) {
        modalHandler().clearAllStatus();
        modalHandler().addErrorMsg("You are in incognito mode");
      } else {
        modalHandler().clearAllStatus();
        modalHandler().addErrorMsg("Your API key is not set in storage");
      }

      context.setIsApiKeyValid(() => false);
      return;
    }

    if (!navigator.onLine) {
      modalHandler().clearAllStatus();
      modalHandler().addErrorMsg("You are offline");
      return;
    }

    await context.fetchApiCheck(() => validateApiKey(apiKey));

    if (context.apiCheckStatus().statusCode === 200) {
      modalHandler().addFillStatusMsg("API token is valid!");
      await startFillAgnostic(modalHandler);
    } else if (context.apiCheckStatus().statusCode === 403) {
      modalHandler().clearFillStatus();
      context.setIsApiKeyValid(() => false);
    } else {
      modalHandler().clearFillStatus();
      modalHandler().addErrorMsg(`Failed to validate API token. Error: ${context.apiCheckStatus().message}`);
    }
  }

  onMount(() => {
    if (document.readyState === "complete") {
      void handleModalStartup();
    } else {
      // window.addEventListener("load", callback, { once: true });
      setTimeout(handleModalStartup, 500);
    }

    document.addEventListener(SHOW_MODAL_EVENT.type, () => void handleModalStartup());
    // document.addEventListener("click", handleDataEntryButtonClick);
    document.addEventListener("keydown", (e) => {
      handleEnterOnTextInput(e);
    });

    const classMutationObserver = new MutationObserverCustom(document.body, {
      attributes: true,
      subtree: true,
      attributeFilter: ["class"],
    });

    classMutationObserver.addHandler(autoScrollToNextAfterPhotoUpload);
  });

  return (
    <div class="bs-5">
      <div
        class="modal fade"
        id="mm-fs-util-modal"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
        tabIndex={-1}
        aria-labelledby="fs-util-modal-label"
        aria-modal="true"
        role="dialog"
        style={{ "max-width": "100vw", "z-index": 999_999_999 }}
      >
        <a
          id="mm-id-open-modal-btn"
          class="btn btn-primary"
          data-bs-toggle="modal"
          href="#mm-fs-util-modal"
          role="button"
          hidden
        >
          Show Modal
        </a>
        <div class="modal-dialog">
          <div class="modal-content bg-dark rounded-4" style={{ "max-height": "90vh" }}>
            <div class="modal-header">
              <h1 class="px-2 modal-title fs-5" id="staticBackdropLabel">
                Distribution Check
              </h1>
              <button
                type="button"
                class="btn-close btn-close-white"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <ModalBody modalHandler={modalHandler} />
            <ModalFooter />
          </div>
        </div>
      </div>
    </div>
  );
}
